import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Loader2, Building2, ArrowRight } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzA4NzQzOHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* LEFT: form */}
      <div className="flex items-center justify-center px-6 sm:px-16 py-16">
        <div className="w-full max-w-md">
          <div className="mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_sales-tracker-1019/artifacts/0i835tsb_f293e0f8-0ee3-473a-b4f8-1de12450a9c6.png"
              alt="Immo Varois — Votre solution locale en immobilier"
              className="h-40 w-auto object-contain"
            />
          </div>

          <p className="label-eyebrow mb-3">Connexion sécurisée</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]">
            Piloter la semaine.<br />
            <span className="text-primary">Sans perdre de temps.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-base leading-relaxed">
            Vos négociateurs remplissent leur point hebdo, vous visualisez leurs efforts et vous préparez la réunion du lundi en 2 minutes.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="vous@agence.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-12 text-base"
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-slate-500">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-12 text-base"
                autoComplete="current-password"
              />
            </div>

            {error ? (
              <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5" data-testid="login-error">
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 text-base gap-2 group"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>

            <div className="text-xs text-slate-500 pt-2">
              Compte administrateur par défaut : <code className="bg-slate-100 px-1.5 py-0.5 rounded">admin@immo.fr</code> / <code className="bg-slate-100 px-1.5 py-0.5 rounded">admin123</code>
            </div>

            <div className="text-sm text-center text-slate-500 pt-1">
              Nouveau négociateur ?{" "}
              <a href="/inscription" className="text-primary font-semibold hover:underline">Créer mon compte</a>
            </div>
          </form>
        </div>
      </div>

      {/* RIGHT: hero image */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 via-slate-900/30 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="label-eyebrow text-white/70 mb-3">Suivi hebdomadaire</div>
          <div className="font-display font-bold text-4xl leading-tight max-w-md">
            Un rapport clair chaque lundi. Une équipe qui avance.
          </div>
          <div className="mt-6 flex gap-8 text-sm">
            <div>
              <div className="font-display font-bold text-3xl">7</div>
              <div className="text-white/70">indicateurs suivis</div>
            </div>
            <div>
              <div className="font-display font-bold text-3xl">2 min</div>
              <div className="text-white/70">pour remplir</div>
            </div>
            <div>
              <div className="font-display font-bold text-3xl">100%</div>
              <div className="text-white/70">visible</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
